/**
 * This component is used to implement in-view tracking that will send data to Rudderstack
 * when the rendered element is in view for half a second. It will fire once by default and
 * can be made to fire multiple times based on the value of the shouldReset function. It will
 * also call a callback function each time after sending an event to Rudderstack.
 *
 * A usage example that was tested in Package/index.tsx:
 *   <InViewTracking
 *     component="package_imaps"
 *      eventData={{ personalize: personalizeOpen }}
 *      shouldReset={(e1, e2) => e1.personalize !== e2.personalize}
 *      callback={() => {
 *        console.log('~~ track callback')
 *      }}
 *    />
 */
import { getVisitorId } from '@ecomm/shared-cookies'
import { useIntersection } from '@ecomm/shared-hooks'
import { getRudderstack } from '@ecomm/shared-window'
import { voidFn } from '@simplisafe/ewok'

import { useEffect, useRef, useState } from 'react'
import type { InViewEventData, InViewEventName } from './types'

type Props<T> = {
  readonly component: string
  readonly eventName?: InViewEventName
  readonly eventData: T
  readonly shouldReset?: (prev: T, curr: T) => boolean
  readonly callback?: () => void
}

export function InViewTracking<Event extends InViewEventData>({
  component,
  eventName = 'in_view',
  eventData,
  shouldReset = () => false,
  callback = voidFn
}: Props<Event>) {
  const [hasFired, setHasFired] = useState(false)
  const [lastState, setLastState] = useState<Event | null>(null)
  const intersectionRef = useRef(null)
  const intersection = useIntersection(intersectionRef, {})
  const payload = {
    component,
    data: eventData,
    vid: getVisitorId()
  }

  useEffect(() => {
    // After the setTimeout elapses, check if the component is still in user's viewport
    const asyncCheck = () => {
      if (intersection?.isIntersecting) {
        getRudderstack(r => {
          r.track(eventName, payload)
        })
        callback()
        setHasFired(true)
      }
    }

    const id =
      !hasFired && intersection?.isIntersecting
        ? setTimeout(asyncCheck, 500)
        : null

    return () => (id ? clearTimeout(id) : void 0)
  }, [callback, eventName, hasFired, intersection, payload])

  if (lastState === null || shouldReset(lastState, eventData)) {
    setLastState(eventData)
    setHasFired(false)
  }

  return <div ref={intersectionRef} />
}
